// pages/MapeoAgencias.js
import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Space,
  Typography,
  message,
  Tag,
  Tooltip,
  Row,
  Col,
  Popconfirm,
  Badge,
  Statistic,
  Card,
  InputNumber,
  Switch,
  Divider,
  Alert
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  ApiOutlined,
  GlobalOutlined,
  DollarOutlined,
  FileTextOutlined,
  UserOutlined,
  TeamOutlined,
  BankOutlined
} from '@ant-design/icons';
import axios from 'axios';

import PageContainer from '../../components/PageContainer';
import { BASE_URL } from '../../utils/constants';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const MapeoAgencias = () => {
  // Estados
  const [mapeos, setMapeos] = useState([]);
  const [hoteles, setHoteles] = useState([]);
  const [hotelesSeleccionados, setHotelesSeleccionados] = useState([]);  
  const [agenciasAnphitrion, setAgenciasAnphitrion] = useState([]);
  const [subagenciasAnphitrion, setSubagenciasAnphitrion] = useState([]);
  const [tiposPago, setTiposPago] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalType, setModalType] = useState('create');
  const [currentMapeo, setCurrentMapeo] = useState(null);
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [filtroHotel, setFiltroHotel] = useState(null);

  // Estados para filtros y ordenación
  const [filteredInfo, setFilteredInfo] = useState({});
  const [sortedInfo, setSortedInfo] = useState({});



  // Columnas de la tabla
  const columns = [
    {
      title: 'Agencia',
      dataIndex: 'Agencia',
      key: 'Agencia',
      width: 120,
      sorter: (a, b) => a.Agencia?.localeCompare(b.Agencia || '') || 0,
      sortOrder: sortedInfo.columnKey === 'Agencia' && sortedInfo.order,
      render: (agencia) => (
        <Tag color="blue" style={{ fontWeight: 'bold', fontSize: '13px' }}>
          {agencia}
        </Tag>
      ),
      fixed: 'left',
    },
    {
      title: 'Agencia XML',
      dataIndex: 'AgenciaXML',
      key: 'AgenciaXML',
      width: 150,
      sorter: (a, b) => a.AgenciaXML?.localeCompare(b.AgenciaXML || '') || 0,
      sortOrder: sortedInfo.columnKey === 'AgenciaXML' && sortedInfo.order,
      render: (agenciaXML) => (
        <Tag color="purple">{agenciaXML}</Tag>
      ),
    },
    {
      title: 'Hotel',
      dataIndex: 'hotel',
      key: 'hotel',
      width: 150,
      sorter: (a, b) => (a.HotelNombre || '').localeCompare(b.HotelNombre || ''),
      sortOrder: sortedInfo.columnKey === 'hotel' && sortedInfo.order,
      render: (hotel, record) => (
        <Tooltip title={`Hotel ID: ${hotel}`}>
          <Tag color="cyan">
            <BankOutlined style={{ marginRight: 4 }} />
            {record.HotelNombre || hotel}
          </Tag>
        </Tooltip>
      ),
      filters: hoteles.map(h => ({ text: h.nombre, value: h.HotelID })),
      filteredValue: filteredInfo.hotel || null,
      onFilter: (value, record) => record.hotel === value,
    },
    {
      title: 'Agencia Anphitrion',
      dataIndex: 'AgenciaAnphitrion',
      key: 'AgenciaAnphitrion',
      width: 180,
      sorter: (a, b) => (a.AgenciaAnphitrion || '').localeCompare(b.AgenciaAnphitrion || ''),
      sortOrder: sortedInfo.columnKey === 'AgenciaAnphitrion' && sortedInfo.order,
      render: (agencia) => (
        agencia ? (
          <Tag color="green">
            <UserOutlined style={{ marginRight: 4 }} />
            {agencia}
          </Tag>
        ) : (
          <Tag color="default">Sin asignar</Tag>
        )
      ),
    },
    {
      title: 'SubAgencia Anphitrion',
      dataIndex: 'SubAgenciaAnphitrion',
      key: 'SubAgenciaAnphitrion',
      width: 180,
      sorter: (a, b) => (a.SubAgenciaAnphitrion || '').localeCompare(b.SubAgenciaAnphitrion || ''),
      sortOrder: sortedInfo.columnKey === 'SubAgenciaAnphitrion' && sortedInfo.order,
      render: (subagencia) => (
        subagencia ? (
          <Tag color="gold">
            <TeamOutlined style={{ marginRight: 4 }} />
            {subagencia}
          </Tag>
        ) : (
          <Tag color="default">Sin asignar</Tag>
        )
      ),
    },
    {
      title: 'Dto %',
      dataIndex: 'Dto',
      key: 'Dto',
      width: 80,
      sorter: (a, b) => (a.Dto || 0) - (b.Dto || 0),
      sortOrder: sortedInfo.columnKey === 'Dto' && sortedInfo.order,
      render: (dto) => (
        dto > 0 ? (
          <Tag color="orange">{dto}%</Tag>
        ) : (
          <Tag color="default">0%</Tag>
        )
      ),
    },
    {
      title: 'Pago',
      dataIndex: 'fpagotexto',
      key: 'fpagotexto',
      width: 120,
      render: (fpagotexto) => (
        <Tag color="geekblue">{fpagotexto || 'Sin definir'}</Tag>
      ),
    },
    {
      title: 'Carga Fecha Límite',
      dataIndex: 'CargarFechaLimitePago',
      key: 'CargarFechaLimitePago',
      width: 130,
      render: (cargar) => (
        cargar ? (
          <Badge status="success" text="Sí" style={{ color: '#52c41a' }} />
        ) : (
          <Badge status="default" text="No" style={{ color: '#d9d9d9' }} />
        )
      ),
      filters: [
        { text: 'Sí', value: true },
        { text: 'No', value: false },
      ],
      filteredValue: filteredInfo.CargarFechaLimitePago || null,
      onFilter: (value, record) => record.CargarFechaLimitePago === value,
    },
    {
      title: 'Modificada',
      dataIndex: 'ModificadaAnphitrion',
      key: 'ModificadaAnphitrion',
      width: 150,
      render: (fecha) => (
        fecha ? new Date(fecha).toLocaleDateString('es-ES') : 'Nunca'
      ),
      sorter: (a, b) => {
        const dateA = a.ModificadaAnphitrion ? new Date(a.ModificadaAnphitrion).getTime() : 0;
        const dateB = b.ModificadaAnphitrion ? new Date(b.ModificadaAnphitrion).getTime() : 0;
        return dateA - dateB;
      },
      sortOrder: sortedInfo.columnKey === 'ModificadaAnphitrion' && sortedInfo.order,
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 180,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Tooltip title="Ver detalles">
            <Button
              type="default"
              icon={<EyeOutlined />}
              size="small"
              onClick={() => handleView(record)}
            />
          </Tooltip>
          
          <Tooltip title="Editar">
            <Button
              type="primary"
              icon={<EditOutlined />}
              size="small"
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          
          <Popconfirm
            title="¿Eliminar mapeo?"
            description={`Esta acción eliminará el mapeo de la agencia ${record.Agencia}`}
            onConfirm={() => handleDelete(record.Agencia)}
            okText="Sí"
            cancelText="No"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Eliminar">
              <Button
                danger
                icon={<DeleteOutlined />}
                size="small"
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Cargar datos iniciales
  useEffect(() => {
    fetchMapeos();
    fetchHoteles();
    // fetchAgenciasAnphitrion();
    // fetchTiposPago();
  }, []);

  // Cargar subagencias cuando cambia la agencia seleccionada
  useEffect(() => {
    const agencia = form.getFieldValue('AgenciaAnphitrion');
    if (agencia) {
      fetchSubagenciasAnphitrion(agencia);
    }
  }, [form.getFieldValue('AgenciaAnphitrion')]);

  const fetchMapeos = async (params = {}) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      const queryParams = {
        search: searchText,
        hoteles: hotelesSeleccionados,
        sortField: params.sortField || sortedInfo.field || 'Agencia',
        sortOrder: params.sortOrder || (sortedInfo.order === 'descend' ? 'DESC' : 'ASC')
      };

      const response = await axios.get(`${BASE_URL}/api/mapeo-agencias`, {
        headers: { Authorization: `Bearer ${token}` },
        params: queryParams
      });

      if (response.data.success) {
        setMapeos(response.data.data || []);
        if (response.data.hoteles) {
          setHoteles(response.data.hoteles);
        }
      }
      
    } catch (error) {
      console.error('❌ Error cargando mapeos:', error);
      message.error('Error al cargar los mapeos');
      setMapeos([]);
    } finally {
      setLoading(false);
      setSearchLoading(false);
    }
  };

  const fetchHoteles = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/hoteles/dropdown`, {
        headers: { Authorization: `Bearer ${token}` }
      });      
      
      setHoteles(response.data.data || []);
    } catch (error) {
      console.error('❌ Error cargando hoteles:', error);
    }
  };

  const fetchAgenciasAnphitrion = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/mapeo-agencias/agencias-anphitrion`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setAgenciasAnphitrion(response.data.data || []);
      }
    } catch (error) {
      console.error('❌ Error cargando agencias Anphitrion:', error);
    }
  };

  const fetchSubagenciasAnphitrion = async (agencia) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/mapeo-agencias/subagencias-anphitrion`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { agencia }
      });
      if (response.data.success) {
        setSubagenciasAnphitrion(response.data.data || []);
      }
    } catch (error) {
      console.error('❌ Error cargando subagencias:', error);
    }
  };

  const fetchTiposPago = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/mapeo-agencias/tipos-pago`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setTiposPago(response.data.data || []);
      }
    } catch (error) {
      console.error('❌ Error cargando tipos de pago:', error);
    }
  };

  // Handlers
  const handleCreate = () => {
    setModalType('create');
    setCurrentMapeo(null);
    form.resetFields();
    form.setFieldsValue({
      Dto: 0,
      fpago: 0,
      CargarFechaLimitePago: false,
      ObservacionesReserva: ''
    });
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setModalType('edit');
    setCurrentMapeo(record);
    
    form.setFieldsValue({
      Agencia: record.Agencia,
      AgenciaXML: record.AgenciaXML,
      AgenciaAnphitrion: record.AgenciaAnphitrion || null,
      SubAgenciaAnphitrion: record.SubAgenciaAnphitrion || null,
      hotel: record.hotel,
      Dto: record.Dto || 0,
      fpago: record.fpago || 0,
      fpagotexto: record.fpagotexto || '',
      CampoPrecio: record.CampoPrecio || '',
      ObservacionesReserva: record.ObservacionesReserva || '',
      CargarFechaLimitePago: record.CargarFechaLimitePago || false
    });

    // Cargar subagencias para la agencia seleccionada
    if (record.AgenciaAnphitrion) {
      fetchSubagenciasAnphitrion(record.AgenciaAnphitrion);
    }

    setModalVisible(true);
  };

  const handleView = (record) => {
    Modal.info({
      title: `Mapeo de Agencia ${record.Agencia}`,
      width: 700,
      icon: <ApiOutlined />,
      content: (
        <div style={{ marginTop: 20 }}>
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <strong>Agencia:</strong>
              <div style={{ marginTop: 4 }}>
                <Tag color="blue" style={{ fontSize: '16px', padding: '4px 12px' }}>
                  {record.Agencia}
                </Tag>
              </div>
            </Col>
            <Col span={12}>
              <strong>Agencia XML:</strong>
              <div style={{ marginTop: 4 }}>
                <Tag color="purple">{record.AgenciaXML}</Tag>
              </div>
            </Col>
            
            <Col span={24}>
              <Divider style={{ margin: '8px 0' }} />
            </Col>

            <Col span={12}>
              <strong>Hotel:</strong>
              <div style={{ marginTop: 4 }}>
                <Tag color="cyan">{record.HotelNombre || record.hotel}</Tag>
              </div>
            </Col>
            <Col span={12}>
              <strong>Descuento:</strong>
              <div style={{ marginTop: 4 }}>
                <Tag color="orange">{record.Dto || 0}%</Tag>
              </div>
            </Col>

            <Col span={24}>
              <Divider style={{ margin: '8px 0' }} />
            </Col>

            <Col span={12}>
              <strong>Agencia Anphitrion:</strong>
              <div style={{ marginTop: 4 }}>
                {record.AgenciaAnphitrion ? (
                  <Tag color="green">{record.AgenciaAnphitrion}</Tag>
                ) : (
                  <Tag color="default">Sin asignar</Tag>
                )}
              </div>
            </Col>
            <Col span={12}>
              <strong>SubAgencia Anphitrion:</strong>
              <div style={{ marginTop: 4 }}>
                {record.SubAgenciaAnphitrion ? (
                  <Tag color="gold">{record.SubAgenciaAnphitrion}</Tag>
                ) : (
                  <Tag color="default">Sin asignar</Tag>
                )}
              </div>
            </Col>

            <Col span={24}>
              <Divider style={{ margin: '8px 0' }} />
            </Col>

            <Col span={12}>
              <strong>Forma de Pago:</strong>
              <div style={{ marginTop: 4 }}>
                <Tag color="geekblue">{record.fpagotexto || 'Sin definir'}</Tag>
              </div>
            </Col>
            <Col span={12}>
              <strong>Campo Precio:</strong>
              <div style={{ marginTop: 4 }}>
                <Tag color="default">{record.CampoPrecio || 'No definido'}</Tag>
              </div>
            </Col>

            <Col span={24}>
              <strong>Cargar Fecha Límite de Pago:</strong>
              <div style={{ marginTop: 4 }}>
                {record.CargarFechaLimitePago ? (
                  <Badge status="success" text="Sí" />
                ) : (
                  <Badge status="default" text="No" />
                )}
              </div>
            </Col>

            {record.ObservacionesReserva && (
              <Col span={24}>
                <strong>Observaciones:</strong>
                <div style={{ 
                  marginTop: 4, 
                  padding: '8px 12px', 
                  backgroundColor: '#f5f5f5', 
                  borderRadius: '4px'
                }}>
                  {record.ObservacionesReserva}
                </div>
              </Col>
            )}

            <Col span={24}>
              <Divider style={{ margin: '8px 0' }} />
              <Text type="secondary" style={{ fontSize: '12px' }}>
                Última modificación: {record.ModificadaAnphitrion ? 
                  new Date(record.ModificadaAnphitrion).toLocaleString('es-ES') : 
                  'Nunca'}
              </Text>
            </Col>
          </Row>
        </div>
      ),
    });
  };

  const handleDelete = async (agencia) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${BASE_URL}/api/mapeo-agencias/${agencia}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setMapeos(mapeos.filter(m => m.Agencia !== agencia));
      message.success('Mapeo eliminado correctamente');
    } catch (error) {
      console.error('❌ Error eliminando mapeo:', error);
      message.error(error.response?.data?.error || 'Error al eliminar el mapeo');
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const token = localStorage.getItem('token');

      // Buscar el texto del tipo de pago seleccionado
      const tipoPago = tiposPago.find(tp => tp.value === values.fpago);
      const fpagotexto = tipoPago ? tipoPago.label : '';

      const dataToSend = {
        ...values,
        fpagotexto,
        AgenciaAnphitrion: values.AgenciaAnphitrion || null,
        SubAgenciaAnphitrion: values.SubAgenciaAnphitrion || null
      };

      if (modalType === 'create') {
        await axios.post(
          `${BASE_URL}/api/mapeo-agencias`,
          dataToSend,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        message.success('Mapeo creado correctamente');
      } else {
        await axios.put(
          `${BASE_URL}/api/mapeo-agencias/${currentMapeo.Agencia}`,
          dataToSend,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        message.success('Mapeo actualizado correctamente');
      }
      
      setModalVisible(false);
      form.resetFields();
      fetchMapeos();
      
    } catch (error) {
      console.error('❌ Error guardando mapeo:', error);
      if (error.response?.data?.error) {
        message.error(error.response.data.error);
      } else {
        message.error('Error al guardar los datos');
      }
    }
  };

  const handleSearch = () => {

    if (hotelesSeleccionados.length === 0) {
      message.warning('Debe seleccionar al menos un hotel');
      return;
    }

    setSearchLoading(true);
    fetchMapeos();
  };

  const handleResetSearch = () => {
    setSearchText('');
    setFiltroHotel(null);
    setFilteredInfo({});
    setSortedInfo({});
    setSearchLoading(false);
    fetchMapeos();
  };

  // Barra de búsqueda
  const searchBar = (
    <div style={{ marginBottom: 16 }}>

      <Row gutter={[16, 16]}>
        
          <Col span={24}>
            <Text strong>Hoteles:</Text>
            <Select
              mode="multiple"
              style={{ width: '100%', marginTop: 8 }}
              placeholder="Seleccionar hoteles..."
              value={hotelesSeleccionados}
              onChange={setHotelesSeleccionados}
              options={hoteles}
              allowClear
            />
          </Col>
      </Row>

        <Button
          type="primary"
          icon={<SearchOutlined />}
          onClick={handleSearch}
          loading={searchLoading}
        >
          Buscar
        </Button>
        <Button
          icon={<ReloadOutlined />}
          onClick={handleResetSearch}
        >
          Restablecer
        </Button>
        <Text type="secondary" style={{ fontSize: '12px' }}>
          {mapeos.length} registros
        </Text>      
    </div>
  );

  return (
    <PageContainer
      title="Mapeo de Agencias"
      subtitle={`${mapeos.length} mapeos registrados`}
      icon={<ApiOutlined />}
      loading={loading}
      onRefresh={() => fetchMapeos()}
      onCreate={handleCreate}
      badgeCount={mapeos.length}
      badgeStatus="processing"
    >

      
      {/* Barra de búsqueda */}
      {searchBar}
      
      {/* Tabla */}
      <Table
        columns={columns}
        dataSource={mapeos}
        rowKey={(record) => record.Agencia}
        loading={loading}
        scroll={{ x: 1400, y: 600 }}
        style={{
          borderRadius: '8px',
          overflow: 'hidden'
        }}
        size="middle"
        locale={{
          emptyText: 'No se encontraron mapeos'
        }}
        onChange={(pagination, filters, sorter) => {
          setFilteredInfo(filters);
          setSortedInfo(sorter);
          fetchMapeos({
            sortField: sorter.field,
            sortOrder: sorter.order === 'ascend' ? 'ASC' : 'DESC'
          });
        }}
      />

      {/* Modal para crear/editar */}
      <Modal
        title={modalType === 'create' ? 'Nuevo Mapeo de Agencia' : 'Editar Mapeo de Agencia'}
        open={modalVisible}
        onOk={handleSave}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        width={700}
        okText={modalType === 'create' ? 'Crear' : 'Guardar'}
        cancelText="Cancelar"
        confirmLoading={loading}
        style={{ top: 20 }}
        bodyStyle={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            Dto: 0,
            fpago: 0,
            CargarFechaLimitePago: false,
            ObservacionesReserva: ''
          }}
        >
          <Alert
            message="Información"
            description="Configure el mapeo entre agencias XML y agencias del sistema Anphitrion"
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="Agencia"
                label="Agencia"
                rules={[{ required: true, message: 'Ingrese el código de agencia' }]}
              >
                <Input 
                  placeholder="Código de agencia"
                  disabled={modalType === 'edit'}
                  prefix={<ApiOutlined />}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="AgenciaXML"
                label="Agencia XML"
                rules={[{ required: true, message: 'Ingrese el código XML' }]}
              >
                <Input 
                  placeholder="Código de agencia XML"
                  prefix={<GlobalOutlined />}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="hotel"
                label="Hotel"
                rules={[{ required: true, message: 'Seleccione un hotel' }]}
              >
                <Select placeholder="Seleccione un hotel">
                  {hoteles.map(hotel => (
                    <Option key={hotel.HotelID} value={hotel.HotelID}>
                      <BankOutlined style={{ marginRight: 8 }} />
                      {hotel.nombre}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="Dto"
                label="Descuento (%)"
                tooltip="Porcentaje de descuento para esta agencia"
              >
                <InputNumber
                  min={0}
                  max={100}
                  style={{ width: '100%' }}
                  placeholder="0"
                  formatter={value => `${value}%`}
                  parser={value => value.replace('%', '')}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="AgenciaAnphitrion"
                label="Agencia Anphitrion"
                tooltip="Seleccione la agencia del sistema Anphitrion"
              >
                <Select
                  placeholder="Seleccione una agencia"
                  showSearch
                  optionFilterProp="label"
                  onChange={(value) => {
                    if (value) {
                      fetchSubagenciasAnphitrion(value);
                    } else {
                      setSubagenciasAnphitrion([]);
                    }
                  }}
                >
                  <Option value={null}>Sin asignar</Option>
                  {agenciasAnphitrion.map(agencia => (
                    <Option key={agencia.value} value={agencia.value}>
                      <UserOutlined style={{ marginRight: 8 }} />
                      {agencia.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="SubAgenciaAnphitrion"
                label="SubAgencia Anphitrion"
                tooltip="Seleccione la subagencia del sistema Anphitrion"
              >
                <Select
                  placeholder="Seleccione una subagencia"
                  showSearch
                  optionFilterProp="label"
                  disabled={!form.getFieldValue('AgenciaAnphitrion')}
                >
                  <Option value={null}>Sin asignar</Option>
                  {subagenciasAnphitrion.map(sub => (
                    <Option key={sub.value} value={sub.value}>
                      <TeamOutlined style={{ marginRight: 8 }} />
                      {sub.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="fpago"
                label="Forma de Pago"
                rules={[{ required: true, message: 'Seleccione una forma de pago' }]}
              >
                <Select placeholder="Seleccione una forma de pago">
                  {tiposPago.map(tp => (
                    <Option key={tp.value} value={tp.value}>
                      {tp.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="CampoPrecio"
                label="Campo Precio"
                tooltip="Campo que se utiliza para obtener el precio"
              >
                <Input 
                  placeholder="Campo de precio"
                  prefix={<DollarOutlined />}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="CargarFechaLimitePago"
                label="Cargar Fecha Límite de Pago"
                valuePropName="checked"
              >
                <Switch
                  checkedChildren="Sí"
                  unCheckedChildren="No"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="ObservacionesReserva"
            label="Observaciones para Reserva"
          >
            <TextArea
              placeholder="Observaciones que se incluirán en la reserva"
              rows={3}
              maxLength={500}
              showCount
            />
          </Form.Item>

          <Card size="small" style={{ marginTop: 16, backgroundColor: '#f6ffed' }}>
            <div style={{ fontSize: '13px', color: '#666' }}>
              <strong>📝 Información importante:</strong>
              <ul style={{ marginTop: 8, marginBottom: 4, paddingLeft: 20 }}>
                <li><strong>Agencia:</strong> Código de agencia en el sistema XML</li>
                <li><strong>Agencia XML:</strong> Código de agencia en el archivo XML</li>
                <li><strong>Agencia Anphitrion:</strong> Agencia del sistema Anphitrion a la que se mapea</li>
                <li><strong>SubAgencia Anphitrion:</strong> Subagencia del sistema Anphitrion (opcional)</li>
                <li><strong>Dto:</strong> Descuento aplicable a esta agencia</li>
                <li><strong>Forma de Pago:</strong> Forma de pago por defecto para esta agencia</li>
              </ul>
            </div>
          </Card>
        </Form>
      </Modal>
    </PageContainer>
  );
};

export default MapeoAgencias;